import type * as React from "react";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getIsApplyEnabled,
  getIsClearEnabled,
  selectActiveFilterConfiguration,
} from "../../../../Search/redux/searchSelectors";
import { MobileTechFilterCategory } from "./MobileTechFilterCategory";
import { type RootState } from "../../../../redux/store";
import { applyTechValueGroup, clearTechValuesGroup } from "../../../../Search/redux/actions";
import { DesktopTechFilterCategory } from "./DesktopTechFilterCategory";
import { isNonNull, toMapFn } from "../../../../utils/reducers";
import { ReduxImperialToggle } from "~/ImperialMetricData/components/ImperialToggle/ImperialToggle";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/utils/screenSizes";
import { useUnitMode } from "~/ImperialMetricData/useUnitMode";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import { resolveResourceURl } from "~/utils/url/urlResolver";
import { Alert } from "~/common/m3/Alert/Alert";
import { isTechValueFilterCategory } from "~/Search/filterConfigModels";

const STATIC_IMAGE = <img alt="" src={resolveResourceURl("/images/dialog_all.gif")} className="w-100" />;

const TECH_FILTERS_ROWS = [
  {
    title: "page.selectionAssistant.techFilter.dimensions",
    items: {
      Metric: ["borediameter", "outsidediameter", "width"],
      Imperial: ["borediameterimperial", "outsidediameterimperial", "widthimperial"],
    },
  },
  {
    title: "page.selectionAssistant.techFilter.load_rating_temperature_limiting_speed",
    items: {
      Metric: ["basicdynamicloadratingradial", "basicstaticloadratingradial", "tempmin"],
      Imperial: ["basicdynamicloadratingradialimperial", "basicstaticloadratingradialimperial", "tempminimperial"],
    },
  },
];

export const DesktopSATechValuesFilter = () => {
  const { t } = useTranslation();
  const unitMode = useUnitMode();
  const techFiltersByCode = useTechFiltersByCode();

  const techValueFilters = Object.values(techFiltersByCode);

  const titles = techValueFilters.map((item) => item.title);
  const isApplyEnabled = useSelector(getIsApplyEnabled);
  const isClearEnabled = useSelector(selectIsClearEnabled(titles));
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const dispatch = useDispatch();
  const applyCb = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    dispatch(applyTechValueGroup());
  }, []);

  const onClearClick = () => dispatch(clearTechValuesGroup(titles));

  if (!Object.values(techFiltersByCode).length) {
    return null;
  }

  return (
    <div>
      {isDesktop && <ReduxImperialToggle className={"mt-30 mb-30"} page={"SA"} />}
      <form onSubmit={applyCb} className="scha__sa__tech-filter-section">
        {TECH_FILTERS_ROWS.map((rowConfig, index) => (
          <DesktopTechFilterCategory
            items={rowConfig.items[unitMode].map((code) => techFiltersByCode[code])}
            image={index === 0 ? STATIC_IMAGE : undefined}
            title={t(rowConfig.title)}
          />
        ))}
        <Alert severity={"info"} className={"mb-3"}>
          {t("page.selectionAssistant.dimensions.blueMessage")}
        </Alert>
        <div className={"scha__sa__tech-filter-section__buttons"}>
          <Button
            disabled={!isClearEnabled}
            onClick={onClearClick}
            variant={"text"}
            data-test-id={"techvaluefilter-clear-button"}
          >
            {t("searchPage.refinement.button.clear")}
          </Button>
          <Button
            disabled={!isApplyEnabled}
            type={"submit"}
            className="ml-30"
            data-test-id={"techvaluefilter-submit-button"}
          >
            {t("searchPage.refinement.button.apply")}
          </Button>
        </div>
      </form>
    </div>
  );
};

interface MobileSATechValuesFilterProps {
  index: number;
}

export const MobileSATechValuesFilter = ({ index }: MobileSATechValuesFilterProps) => {
  const unitMode = useUnitMode();
  const techFiltersByCode = useTechFiltersByCode();

  const items = TECH_FILTERS_ROWS[index].items[unitMode].map((code) => techFiltersByCode[code]).filter(isNonNull);

  if (!items.length) {
    return null;
  }

  const image = index === 0 ? STATIC_IMAGE : null;

  return (
    <div>
      <MobileTechFilterCategory key={index} items={items} image={image} />
    </div>
  );
};

const selectIsClearEnabled = (title: string | string[]) => (state: RootState) => getIsClearEnabled(state, title);

function useTechFiltersByCode() {
  const filterConfig = useSelector(selectActiveFilterConfiguration);
  const techFiltersByCode = useMemo(() => {
    const techValueFilters = filterConfig?.filter(isTechValueFilterCategory) ?? [];
    return toMapFn(
      techValueFilters.flatMap((category) => category.items),
      (item) => item.uid,
    );
  }, [filterConfig]);

  return techFiltersByCode;
}
